import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faCircleDollarToSlot, faClipboardCheck, faClipboardQuestion, faUsers } from "@fortawesome/free-solid-svg-icons";

import { RootState } from "../../../../globals";
import { getAccessPermissions } from "../../../../common";
import Card from "../../../../components/card";
import { BarraFiltros, BuscadorBarra, FiltroMulti } from "../../../../components/buscadorBarra/buscadorBarra";
import useEsTelefono from "../../../../utils/esTelefono";
import { Clase, calendarioApi, olvidarAlumnos } from "./calendarioApi";
import { useGlobito } from "./globito";
import {
	DIAS_CORTOS, JORNADAS, JornadaKey, bajoMinimo, dinero, diaSemana, fechaCorta, fechaDe, fechaLarga, jornadaDe, lunesDe, maestraDe,
	coincideCalendario, horaClase, sinPasar, subClase, sumarDias, tituloClase, tramoClase
} from "./formato";
import { ChipSemana, Contexto, Tarjeta, llaveClase } from "./piezas";
import PanelClase from "./panelClase";
import "../../managment/enrollmentRequests/filtrosMatriculas.css";
import "./calendario.css";

/*
	Calendario Claro (Esteban, 2026-11-01/02): reemplaza a «Por Día» y «Por Semana» (navigation/calendar, que se queda en el
	código una semana por si hay que regresar). Las dos entradas del menú abren esta pantalla, cada una en su vista.
	Tarjetas con el nombre del curso y su icono; la hora sale del nombre del horario; morosidad y asistencia abren su globito
	con el ratón o con clic; clic en la tarjeta abre el panel con la lista. Filtros y buscador trabajan en el navegador.
	Enlace: ?fecha=AAAA-MM-DD&horario=<id> abre ese día con el panel de la clase (lo usa Campus → Aulas → Ocupación).
*/

export type Vista = "dia" | "semana";
type Estado = "vencido" | "kit" | "sinPasar" | "bajoMinimo" | "sinMaestra";
const ESTADOS: { id: Estado; name: string }[] = [
	{ id: "vencido", name: "Saldo Vencido" }, { id: "kit", name: "Kit Pendiente" }, { id: "sinPasar", name: "Lista Sin Pasar" },
	{ id: "bajoMinimo", name: "Bajo Mínimo" }, { id: "sinMaestra", name: "Sin Maestra" },
];
const SIN_MAESTRA = "Sin Maestra";
const LIMITE_CELDA = 8;

const hoyLocal = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const ordenar = (a: Clase, b: Clase) => tramoClase(a)[0] - tramoClase(b)[0] || tituloClase(a).localeCompare(tituloClase(b), "es");

interface Enlace { fecha?: string; horario?: number; }
// La pantalla se monta dos veces al entrar con un enlace (el menú se arma después de iniciar sesión) y la segunda vez la
// dirección ya está limpia: el enlace se recuerda unos segundos, como en Actualizaciones.
let enlaceRecordado: { enlace: Enlace; en: number } | null = null;
const leerEnlace = (): Enlace => {
	const q = new URLSearchParams(window.location.search);
	const fecha = /^\d{4}-\d{2}-\d{2}$/.test(q.get("fecha") || "") ? q.get("fecha")! : undefined;
	const horario = Number(q.get("horario") || 0) || undefined;
	if (fecha || horario) {
		window.history.replaceState(null, "", window.location.pathname);
		enlaceRecordado = { enlace: { fecha, horario }, en: Date.now() };
		return { fecha, horario };
	}
	if (enlaceRecordado && Date.now() - enlaceRecordado.en < 60000) return enlaceRecordado.enlace;
	return {};
};

const CalendarioController: React.FC<{ inicial: Vista }> = ({ inicial }) => {
	const movil = useEsTelefono();
	const usuario: any = useSelector((s: RootState) => s.userProfile.value);
	const campusKey: any = useSelector((s: RootState) => s.currentUserCampus.value);
	const campusRef: any = useSelector((s: RootState) => s.campusRef.value);
	const campus: any = campusKey && campusRef ? campusRef[campusKey] : null;
	const campusId: number = campus?.id || 0;
	// Online (5) cobra en lempiras como Honduras.
	const countryId: number | null = campusId === 5 ? 2 : campus?.company?.country_id ?? null;
	const pasarLista = !!getAccessPermissions(usuario, "attendances").read;
	const verHorario = !!getAccessPermissions(usuario, "schedules").read;

	const [enlace] = useState(leerEnlace);
	// Si la pantalla se queda montada, el enlace ya se usó: no se repite al volver desde el menú.
	useEffect(() => { const t = setTimeout(() => { enlaceRecordado = null; }, 4000); return () => clearTimeout(t); }, []);
	const [vista, setVista] = useState<Vista>(enlace.fecha ? "dia" : inicial);
	const [fecha, setFecha] = useState<string>(enlace.fecha || hoyLocal());
	const [hoy, setHoy] = useState<string>(hoyLocal());
	const [clases, setClases] = useState<Clase[] | null>(null);
	// Vista que trajo `clases`: al cambiar de Día a Semana no se pinta la semana con el acomodo del día mientras llega.
	const [vistaDatos, setVistaDatos] = useState<Vista>(vista);
	const [cargando, setCargando] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [sel, setSel] = useState<string | null>(null);
	const pendienteAbrir = useRef<number | undefined>(enlace.horario);
	const [abiertas, setAbiertas] = useState<Set<string>>(new Set());

	const [q, setQ] = useState("");
	const [fJornadas, setFJornadas] = useState<JornadaKey[]>([]);
	const [fMaestras, setFMaestras] = useState<string[]>([]);
	const [fCursos, setFCursos] = useState<string[]>([]);
	const [fEdificios, setFEdificios] = useState<number[]>([]);
	const [fPeriodos, setFPeriodos] = useState<number[]>([]);
	const [fEstados, setFEstados] = useState<Estado[]>([]);
	const hayFiltros = q !== "" || fJornadas.length + fMaestras.length + fCursos.length + fEdificios.length + fPeriodos.length + fEstados.length > 0;
	const limpiar = () => { setQ(""); setFJornadas([]); setFMaestras([]); setFCursos([]); setFEdificios([]); setFPeriodos([]); setFEstados([]); };

	const globito = useGlobito();
	const pedido = useRef(0);
	const desdeSemana = lunesDe(fecha);

	const cargar = useCallback(async () => {
		if (!campusId) return;
		const n = ++pedido.current;
		setCargando(true);
		try {
			const r = vista === "dia" ? await calendarioApi.dia(campusId, fecha) : await calendarioApi.semana(campusId, fecha);
			if (n !== pedido.current) return;
			setClases(r.data.clases);
			setVistaDatos(vista);
			setHoy(r.data.hoy);
			setError(null);
			if (pendienteAbrir.current) {
				const c = r.data.clases.find((x) => x.horarioId === pendienteAbrir.current);
				if (c) setSel(llaveClase(c));
				pendienteAbrir.current = undefined;
			}
		} catch (e) {
			if (n !== pedido.current) return;
			setError("No se pudo cargar el calendario.");
			setClases([]);
			setVistaDatos(vista);
		} finally {
			if (n === pedido.current) setCargando(false);
		}
	}, [campusId, vista, vista === "dia" ? fecha : desdeSemana]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => { cargar(); }, [cargar]);
	// Otro plantel: nada de lo anterior aplica.
	useEffect(() => { setClases(null); setSel(null); olvidarAlumnos(); limpiar(); }, [campusId]); // eslint-disable-line react-hooks/exhaustive-deps
	useEffect(() => { globito.cerrar(); }, [vista, fecha]); // eslint-disable-line react-hooks/exhaustive-deps

	// ---------- filtros (en el navegador) ----------
	const pasa = useCallback((c: Clase) => {
		if (fJornadas.length && !fJornadas.includes(jornadaDe(c))) return false;
		if (fMaestras.length && !fMaestras.includes(maestraDe(c) || SIN_MAESTRA)) return false;
		if (fCursos.length && !fCursos.includes(tituloClase(c))) return false;
		if (fEdificios.length && !fEdificios.includes(c.edificio?.id ?? 0)) return false;
		if (fPeriodos.length && !fPeriodos.includes(c.periodo.id)) return false;
		if (fEstados.length && !fEstados.some((e) =>
			(e === "vencido" && (c.vencido?.alumnos ?? 0) > 0) || (e === "kit" && (c.kits?.alumnos ?? 0) > 0) || (e === "sinPasar" && sinPasar(c))
			|| (e === "bajoMinimo" && bajoMinimo(c)) || (e === "sinMaestra" && !c.maestra))) return false;
		// Solo lo que se ve en la tarjeta (el módulo no sale, salvo en Instituto, donde es el título) más las alumnas.
		return coincideCalendario(q, tituloClase(c), subClase(c), horaClase(c), maestraDe(c) || SIN_MAESTRA, c.aula?.nombre, c.edificio?.nombre, ...(c.alumnos || []));
	}, [q, fJornadas, fMaestras, fCursos, fEdificios, fPeriodos, fEstados]);

	const visibles = useMemo(() => (clases || []).filter(pasa).sort(ordenar), [clases, pasa]);

	const opciones = useMemo(() => {
		const cs = clases || [];
		const unicos = <T,>(xs: T[]) => Array.from(new Set(xs));
		return {
			jornadas: JORNADAS.filter((j) => cs.some((c) => jornadaDe(c) === j.key)).map((j) => ({ id: j.key, name: j.nombre })),
			maestras: unicos(cs.map((c) => maestraDe(c) || SIN_MAESTRA)).sort((a, b) => a.localeCompare(b, "es")).map((m) => ({ id: m, name: m })),
			cursos: unicos(cs.map(tituloClase)).filter(Boolean).sort((a, b) => a.localeCompare(b, "es")).map((m) => ({ id: m, name: m })),
			edificios: Array.from(new Map(cs.filter((c) => c.edificio).map((c) => [c.edificio!.id, c.edificio!.nombre])).entries())
				.sort((a, b) => a[1].localeCompare(b[1], "es")).map(([id, name]) => ({ id, name })),
			periodos: Array.from(new Map(cs.map((c) => [c.periodo.id, c.periodo.nombre])).entries()).map(([id, name]) => ({ id, name })),
		};
	}, [clases]);

	const seleccionada = useMemo(() => (sel ? (clases || []).find((c) => llaveClase(c) === sel) || null : null), [sel, clases]);

	const ctx: Contexto = {
		campusId, countryId, globito,
		onAbrir: (c) => {
			if (vista === "semana") { setVista("dia"); setFecha(c.fecha); }
			setSel(llaveClase(c));
		},
	};

	// ---------- cifras de arriba ----------
	const cifras = () => {
		const porHorario = Array.from(new Map(visibles.map((c) => [c.horarioId, c])).values());
		const alumnos = porHorario.reduce((s, c) => s + c.activos, 0);
		// Lo vencido es de la alumna en el plantel: si está en dos clases, se cuenta una vez.
		const deudas = new Map<string, number>();
		porHorario.forEach((c) => Object.entries(c.vencido?.porAlumno || {}).forEach(([id, v]) => deudas.set(id, v)));
		const nv = deudas.size;
		const tv = Array.from(deudas.values()).reduce((s, v) => s + v, 0);
		const conAlumnos = visibles.filter((c) => c.activos > 0 && c.lista);
		const pasadas = conAlumnos.filter((c) => c.lista!.pasada).length;
		const cajas: { lbl: string; val: React.ReactNode; cls?: string }[] = [{ lbl: "Clases", val: visibles.length }];
		if (vista === "dia") {
			cajas.push({ lbl: "Alumnos", val: alumnos.toLocaleString("en-US") });
			cajas.push(conAlumnos.length ? { lbl: "Lista Pasada", val: <>{pasadas}<small> de {conAlumnos.length}</small></>, cls: pasadas < conAlumnos.length ? "oro" : "" } : { lbl: "Lista Pasada", val: "—" });
		} else {
			cajas.push({ lbl: "Horarios", val: porHorario.length });
			cajas.push({ lbl: "Alumnos", val: alumnos.toLocaleString("en-US") });
		}
		cajas.push({ lbl: "Saldo Vencido", val: <>{nv}<small> {nv === 1 ? "alumna" : "alumnos"} · {dinero(tv, countryId)}</small></>, cls: nv ? "malo" : "" });
		return <div className="calcCifras">{cajas.map((k) => <div key={k.lbl} className={"calcCifra " + (k.cls || "")}><span>{k.lbl}</span><b>{k.val}</b></div>)}</div>;
	};

	// ---------- vistas ----------
	const dia = () => {
		if (visibles.length < 1) return <div className="calcVacio">{hayFiltros ? "Sin clases con estos filtros." : "No hay clases este día."}</div>;
		return JORNADAS.map((j) => {
			const g = visibles.filter((c) => jornadaDe(c) === j.key);
			if (g.length < 1) return null;
			return (
				<section className="calcJornada" key={j.key}>
					<div className="calcJornadaCab"><h5>{j.nombre}</h5><span>{j.rango} · {g.length === 1 ? "1 clase" : `${g.length} clases`}</span></div>
					<div className="calcTarjetas">{g.map((c) => <Tarjeta key={llaveClase(c)} c={c} ctx={ctx} elegida={sel === llaveClase(c)} pasarLista={pasarLista} />)}</div>
				</section>
			);
		});
	};

	const semana = () => {
		const dias = Array.from({ length: 7 }, (_, i) => sumarDias(desdeSemana, i));
		const jornadas = JORNADAS.filter((j) => visibles.some((c) => jornadaDe(c) === j.key));
		if (jornadas.length < 1) return <div className="calcVacio">{hayFiltros ? "Sin clases con estos filtros." : "No hay clases esta semana."}</div>;
		// En el teléfono siete columnas no caben: un día debajo de otro, con sus clases de dos en dos.
		if (movil) {
			return dias.filter((d) => visibles.some((c) => c.fecha === d)).map((d) => {
				const delDia = visibles.filter((c) => c.fecha === d);
				return (
					<section className="calcJornada" key={d}>
						<button type="button" className={"calcDiaMovil" + (d === hoy ? " hoy" : "")} onClick={() => { setVista("dia"); setFecha(d); }}>
							<h5>{fechaLarga(d)}</h5><span>{delDia.length === 1 ? "1 clase" : `${delDia.length} clases`}</span>
						</button>
						{JORNADAS.filter((j) => delDia.some((c) => jornadaDe(c) === j.key)).map((j) => (
							<div key={j.key} className="calcSemanaMovilJornada">
								<small>{j.nombre}</small>
								<div className="calcChipsMovil">{delDia.filter((c) => jornadaDe(c) === j.key).map((c) => <ChipSemana key={llaveClase(c)} c={c} ctx={ctx} />)}</div>
							</div>
						))}
					</section>
				);
			});
		}
		return (
			<div className="calcSemanaMarco">
				<div className="calcSemana">
					<div className="calcEsquina" />
					{dias.map((d) => (
						<button type="button" key={d} className={"calcDiaCab" + (d === hoy ? " hoy" : "")} onClick={() => { setVista("dia"); setFecha(d); }} title="Ver el día">
							{DIAS_CORTOS[diaSemana(d)]} {fechaDe(d).getDate()}<small>{visibles.filter((c) => c.fecha === d).length}</small>
						</button>
					))}
					{jornadas.map((j) => (
						<React.Fragment key={j.key}>
							<div className="calcJornadaLado"><b>{j.nombre}</b>{j.rango.replace(" – ", "–")}</div>
							{dias.map((d) => {
								const g = visibles.filter((c) => c.fecha === d && jornadaDe(c) === j.key);
								const llave = j.key + d;
								const ver = abiertas.has(llave) || g.length <= LIMITE_CELDA + 1 ? g : g.slice(0, LIMITE_CELDA);
								return (
									<div key={llave} className={"calcCelda" + (d === hoy ? " hoy" : "")}>
										{ver.map((c) => <ChipSemana key={llaveClase(c)} c={c} ctx={ctx} />)}
										{g.length > ver.length ? <button type="button" className="calcMas" onClick={() => setAbiertas(new Set(abiertas).add(llave))}>+{g.length - ver.length} más</button> : null}
									</div>
								);
							})}
						</React.Fragment>
					))}
				</div>
			</div>
		);
	};

	const mover = (n: number) => { setSel(null); setFecha(sumarDias(fecha, vista === "dia" ? n : n * 7)); };
	const elegirFecha = useRef<HTMLInputElement | null>(null);
	const titulo = vista === "dia" ? fechaLarga(fecha) : `${fechaCorta(desdeSemana)} – ${fechaCorta(sumarDias(desdeSemana, 6))}`;

	return (
		<div className={"container-fluid waModulo calc" + (movil ? " movil" : "") + (seleccionada ? " conPanel" : "")}>
			<div className="calcEncabezado">
				<div className="calcEncabezadoIzq">
					<h4>Calendario</h4>
					<div className="calcSeg" role="group" aria-label="Vista">
						<button type="button" aria-pressed={vista === "dia"} onClick={() => setVista("dia")}>Día</button>
						<button type="button" aria-pressed={vista === "semana"} onClick={() => { setVista("semana"); setSel(null); }}>Semana</button>
					</div>
				</div>
				<div className="calcNav">
					<button type="button" className="calcHoy" onClick={() => { setSel(null); setFecha(hoy); }}>Hoy</button>
					<button type="button" className="calcRedondo" aria-label="Anterior" onClick={() => mover(-1)}><FontAwesomeIcon icon={faChevronLeft} /></button>
					<button type="button" className="calcRedondo" aria-label="Siguiente" onClick={() => mover(1)}><FontAwesomeIcon icon={faChevronRight} /></button>
					<button type="button" className="calcFecha" title="Elegir fecha" onClick={() => { const i = elegirFecha.current as any; if (i?.showPicker) { try { i.showPicker(); return; } catch (e) { /* se cae al foco */ } } i?.focus(); }}>{titulo}</button>
					<input ref={elegirFecha} type="date" className="calcFechaOculta" value={fecha} tabIndex={-1} aria-hidden="true"
						onChange={(e) => { if (e.target.value) { setSel(null); setFecha(e.target.value); } }} />
				</div>
			</div>

			{error ? <div className="alert alert-danger" style={{ fontSize: 15 }}>{error}</div> : null}

			<Card>
				<div className={"calcCuerpo" + (cargando && clases ? " cargando" : "")}>
					<BarraFiltros hayFiltros={hayFiltros} onLimpiar={limpiar}>
						<BuscadorBarra valor={q} onBuscar={setQ} placeholder={vista === "dia" ? "Buscar clase o alumna" : "Buscar clase"} />
						<FiltroMulti titulo="Jornada" opciones={opciones.jornadas} elegidos={fJornadas} onChange={setFJornadas} />
						<FiltroMulti titulo="Maestra" opciones={opciones.maestras} elegidos={fMaestras} onChange={setFMaestras} buscar />
						<FiltroMulti titulo="Curso" opciones={opciones.cursos} elegidos={fCursos} onChange={setFCursos} buscar />
						<FiltroMulti titulo="Edificio" opciones={opciones.edificios} elegidos={fEdificios} onChange={setFEdificios} />
						<FiltroMulti titulo="Periodo" opciones={opciones.periodos} elegidos={fPeriodos} onChange={setFPeriodos} />
						<FiltroMulti titulo="Estado" opciones={ESTADOS} elegidos={fEstados} onChange={setFEstados} />
					</BarraFiltros>
					{clases === null || vistaDatos !== vista ? <div className="loader" /> : (
						<>
							{cifras()}
							<div className="calcLeyenda" aria-label="Qué significa cada icono">
								<span><FontAwesomeIcon icon={faUsers} /> Alumnos / cupo</span>
								<span><FontAwesomeIcon icon={faClipboardCheck} /> Asistencia</span>
								<span className="calcOro"><FontAwesomeIcon icon={faClipboardQuestion} /> Lista sin pasar</span>
								<span className="calcVencidoTxt"><FontAwesomeIcon icon={faCircleDollarToSlot} /> Saldo vencido</span>
							</div>
							{vista === "dia" ? dia() : semana()}
						</>
					)}
				</div>
			</Card>
			<div className="waSobreDock" />

			{seleccionada ? <PanelClase c={seleccionada} ctx={ctx} pasarLista={pasarLista} verHorario={verHorario} onCerrar={() => setSel(null)} /> : null}
			{globito.capa}
		</div>
	);
};

export const CalendarioDia: React.FC = () => <CalendarioController inicial="dia" />;
export const CalendarioSemana: React.FC = () => <CalendarioController inicial="semana" />;

export default CalendarioController;
